import { useRef, useEffect } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { compose } from '@reduxjs/toolkit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Button from 'core/buttons/Button';
import TextButton from 'core/buttons/TextButton';
import FormContainer from 'core/forms/FormContainer';
import FormTextField from 'hook-form-controls/FormTextField';
import FormSelectPartner from 'hook-form-controls/FormSelectPartner';
import AuthUserIsSuperAdmin from 'components/auth/AuthUserIsSuperAdmin';
import ValidationErrorMessages from 'core/errors/ValidationErrorMessages';

import Auth from 'utils/Auth';
import app from 'app/config/app';
import YupPassword from 'utils/YupPassword';
import useQueryParams from 'hooks/useQueryParams';
import {
  createWebhookHeader,
  updateWebhookHeader,
} from 'app/features/webhook-headers/webhookHeaderSaga';

YupPassword(Yup);

const webhookHeaderSchema = Yup.object().shape({
  partner_id: Yup.string().when([], {
    is: () => Auth.isSuperAdmin(),
    then: Yup.string().required('Select a partner'),
  }),
  custom_header: Yup.string().required('Custom Header is required').min(1).max(50),
  value: Yup.string().required('Value is required').min(1).max(50),
  description: Yup.string().nullable().optional().max(50),
});

function WebhookHeaderForm({ isAddMode, webhookHeader, retries, isCreated }) {
  const submitButtonRef = useRef(null);

  const [query] = useQueryParams();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: {
      ...(isAddMode && query.partner_id ? { partner_id: query.partner_id } : {}),
      ...(webhookHeader ? webhookHeader : {}),
    },
    resolver: yupResolver(webhookHeaderSchema),
    mode: 'onChange',
  });

  const {
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
  } = methods;

  const onSubmit = async (data) => {
    dispatch(
      isAddMode
        ? createWebhookHeader(data)
        : updateWebhookHeader({ ...data, id: webhookHeader.id }),
    );
  };

  useEffect(() => {
    if (retries > 0) submitButtonRef.current.click();
  }, [retries]);

  useEffect(() => {
    if (isCreated) reset({ partner_id: getValues('partner_id') });
  }, [isCreated]);

  return (
    <FormContainer>
      {app.isDevelopmentMode() && <ValidationErrorMessages errors={errors} />}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container rowSpacing={3} columnSpacing={4}>
            <AuthUserIsSuperAdmin>
              <Grid item sm={12}>
                <FormSelectPartner
                  id="select-partner"
                  name="partner_id"
                  label="Select Partner"
                  disabled={isAddMode}
                />
              </Grid>
            </AuthUserIsSuperAdmin>
            <Grid item sm={12} md={6}>
              <FormTextField name="custom_header" label="Custom Header" />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormTextField name="value" label="Value" />
            </Grid>
            <Grid item sm={12}>
              <FormTextField name="description" label="Description" multiline />
            </Grid>
            <Grid item sm={12} md={12}>
              <Box display="flex" justifyContent="space-between">
                <TextButton type="button" color="error" onClick={() => navigate(-1)}>
                  Cancel
                </TextButton>
                <Button
                  ref={submitButtonRef}
                  type="submit"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                >
                  {isAddMode ? 'Create New Webhook Header' : 'Update Webhook Header'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </FormContainer>
  );
}

export default compose(connect(mapStateToProps))(WebhookHeaderForm);

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    retries: ownProps.isAddMode
      ? state.webhookHeaders.create.retries
      : state.webhookHeaders.update.retries,
    isCreated: state.webhookHeaders.create.isCreated,
  };
}

WebhookHeaderForm.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
  webhookHeader: PropTypes.object,
  retries: PropTypes.number,
  isCreated: PropTypes.bool,
};
